import { useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import DownloadIcon from '@mui/icons-material/Download';
import FolderZipIcon from '@mui/icons-material/FolderZip';

import { extractErrorMessage } from '../api/errors';
import {
  getRecordingBundleDownload,
  listRecordingBundles,
  type RecordingBundle,
} from '../api/recordingBundles';
import { saveFile } from '../hooks/usePreparedDownload';
import { ExportDataButton } from './ExportDataButton';

/** "1.2 GB", "340 MB", "12 KB" — an approximate size, so one decimal at most. */
export function approxSize(bytes: number): string {
  if (bytes <= 0) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let v = bytes;
  let i = 0;
  while (v >= 1000 && i < units.length - 1) {
    v /= 1000;
    i += 1;
  }
  return `${v >= 10 || i === 0 ? Math.round(v) : v.toFixed(1)} ${units[i]}`;
}

/**
 * "Download recordings" (TENANT-RECORDINGS-DOWNLOAD) — the tenant's one
 * download button. The recordings come one ZIP per participant (a tenant's
 * video runs to tens of GB, far too much for one file), each with a folder
 * per session holding what the single-session download holds. The first row
 * is the small tables export for everyone (scores, consents, surveys), which
 * used to be its own "Export all data" button.
 */
export function RecordingsDownloadDialog({
  tenantId,
  disabled = false,
}: {
  tenantId: string;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState<RecordingBundle[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [starting, setStarting] = useState<string | null>(null);
  const [started, setStarted] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!open) return;
    let alive = true;
    setRows(null);
    setError(null);
    listRecordingBundles(tenantId)
      .then((r) => alive && setRows(r))
      .catch((e) => alive && setError(extractErrorMessage(e, 'could not load the participants')));
    return () => {
      alive = false;
    };
  }, [open, tenantId]);

  const download = async (row: RecordingBundle) => {
    setStarting(row.key);
    setError(null);
    try {
      const { url, filename } = await getRecordingBundleDownload(tenantId, row.key);
      saveFile(url, filename);
      setStarted((s) => new Set(s).add(row.key));
    } catch (e) {
      setError(extractErrorMessage(e, `could not start the download for ${row.name}`));
    } finally {
      setStarting(null);
    }
  };

  return (
    <>
      <Button
        variant="outlined"
        size="medium"
        startIcon={<FolderZipIcon />}
        onClick={() => setOpen(true)}
        disabled={disabled}
      >
        Download recordings
      </Button>
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Download recordings</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            One file per participant, with a folder for each of their sessions. Each folder
            holds what a single session download holds: the sensor recording, the session
            video and audio, and the participant’s consent and surveys. Files with video can
            be large and take a while.
          </Typography>
          <Stack
            direction="row"
            alignItems="center"
            spacing={2}
            sx={{ mb: 2, p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}
          >
            <Typography variant="body2" sx={{ flex: 1 }}>
              <strong>Everyone, as tables</strong> — sessions, scores, consents and survey
              answers for all participants in one small file.
            </Typography>
            <ExportDataButton scope={{ tenantId }} label="Tables" size="small" />
          </Stack>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {rows === null && !error ? (
            <Stack alignItems="center" sx={{ py: 3 }}>
              <CircularProgress size={24} />
            </Stack>
          ) : rows !== null && rows.length === 0 ? (
            <Alert severity="info">No recordings in this organisation yet.</Alert>
          ) : rows !== null ? (
            <Table size="small" aria-label="Recordings by participant">
              <TableHead>
                <TableRow>
                  <TableCell>Participant</TableCell>
                  <TableCell align="right">Sessions</TableCell>
                  <TableCell align="right">Approx. size</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.key}>
                    <TableCell>{r.name}</TableCell>
                    <TableCell align="right">{r.sessions}</TableCell>
                    <TableCell align="right">{approxSize(r.approx_bytes)}</TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        startIcon={
                          starting === r.key ? <CircularProgress size={14} /> : <DownloadIcon />
                        }
                        disabled={starting !== null}
                        onClick={() => void download(r)}
                        aria-label={`Download recordings of ${r.name}`}
                      >
                        {started.has(r.key) ? 'Download again' : 'Download'}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
